/**
 * Spatial2 registration math — pure and renderer-agnostic, so it can be unit
 * tested and shared between the live renderer and any harness.
 *
 * Scene model: the fg plane sits at world z = zFg and the bg plane at world
 * z = zBg (further from the camera), with the camera on the +z axis at
 * world z = zCam looking at the origin. For the bg plane to project
 * PIXEL-IDENTICALLY over the fg plane at rest (the H2 registration fix — the
 * live bug was a 3.31% perspective scale mismatch), the bg quad's linear scale
 * must equal the ratio of camera distances.
 *
 * NEVER hardcode the nominal (CAM+sep)/CAM = 1.0887: the content group's FILL
 * transform rescales child z, so the distances MUST be measured in WORLD space
 * after all transforms (three.js `getWorldPosition`) and fed here.
 */

export interface WorldPoint {
  x: number;
  y: number;
  z: number;
}

/** Camera description for the pure pinhole projection below. */
export interface RestCamera {
  /** Camera world z (on the +z axis, looking at the origin down −z). */
  zCam: number;
  /** Vertical field of view in degrees. */
  fovDeg: number;
  /** Viewport aspect (width / height). */
  aspect: number;
}

/**
 * Linear scale for the bg plane so it projects exactly onto the fg plane
 * through the rest camera: k = distCam(bg) / distCam(fg).
 * All z values are WORLD-space, measured after every parent transform.
 */
export function bgRegistrationScale(zCam: number, zFgWorld: number, zBgWorld: number): number {
  const dFg = zCam - zFgWorld;
  const dBg = zCam - zBgWorld;
  if (dFg <= 0 || dBg <= 0) {
    throw new Error('registration: both planes must sit in front of the camera');
  }
  return dBg / dFg;
}

/** The 4 corners of an axis-aligned, origin-centred plane of size w×h at world z. */
export function planeCorners(w: number, h: number, z: number): [WorldPoint, WorldPoint, WorldPoint, WorldPoint] {
  return [
    { x: -w / 2, y: -h / 2, z },
    { x: +w / 2, y: -h / 2, z },
    { x: +w / 2, y: +h / 2, z },
    { x: -w / 2, y: +h / 2, z },
  ];
}

/**
 * Pinhole projection of a world point to NDC through the rest camera —
 * identical (up to fp precision) to THREE.PerspectiveCamera + Vector3.project
 * for points between the near/far planes.
 */
export function projectToNdc(p: WorldPoint, cam: RestCamera): { x: number; y: number } {
  const f = 1 / Math.tan((cam.fovDeg * Math.PI) / 360);
  const dz = cam.zCam - p.z;
  if (dz <= 0) throw new Error('registration: point behind the camera');
  return { x: (p.x * f) / (cam.aspect * dz), y: (p.y * f) / dz };
}
